import { useCallback, useEffect, useRef, useState } from 'react'
import { auth, AuthError, type GoogleProfile } from './lib/google/auth'
import { findDoc, rootFolder, uploadFile, type DriveFile } from './lib/google/drive'
import { openDatabase, type Database } from './lib/google/sheets'
import { keyStore } from './lib/keyStore'
import { client } from './lib/claude'
import { readDueDates, run, scan, type FileProgress, type FileStatus, type RunContext } from './lib/pipeline'
import { billsDue, missingDueDates } from './lib/bills'
import { BillsDue } from './Bills'
import { lastAdvice, review, saveReview, setStatus, type AdviceRun, type ReviewProgress, type Saved } from './lib/advice'
import type { LedgerRow } from './lib/analysis'
import { readBook, type Book } from './lib/book'
import { tiller } from './lib/tiller'
import { clearCorrection, deleteRule, saveCorrection, saveRule } from './lib/corrections'
import { TransactionsDrawer, type Correction, type Filter, type NewRule } from './Transactions'
import { saveCategory } from './lib/categories'
import type { CategoryChoice } from './CategoryPicker'
import { Dashboard } from './Dashboard'
import { merchantFilter } from './MerchantSearch'
import { Ask } from './Ask'
import { ProfilePage } from './ProfilePage'
import { accountKey, overdueStatements, type Overdue } from './lib/staleness'
import { formatDate } from './lib/dates'
import { DOC_NAME, publishDoc, sendDigest } from './lib/report'
import { sheetMeter } from './lib/usage'

type Phase =
  | { kind: 'loading'; message: string }
  | { kind: 'signedOut'; error?: string }
  | { kind: 'needsKey'; error?: string }
  | { kind: 'ready' }
  | { kind: 'failed'; error: string }

/** Icon + word for every status; hue is never the only signal. */
const STATUS: Record<FileStatus, { icon: string; label: string }> = {
  uploading: { icon: '↑', label: 'Uploading' },
  waiting: { icon: '○', label: 'Waiting' },
  reading: { icon: '◐', label: 'Reading' },
  filed: { icon: '✓', label: 'Filed' },
  review: { icon: '!', label: 'Needs review' },
  duplicate: { icon: '=', label: 'Duplicate' },
  error: { icon: '✕', label: 'Error' },
  skipped: { icon: '–', label: 'Skipped' },
}

/** The category_id to save: a new category is added to the Categories tab first. */
const categoryId = (db: Database, c: CategoryChoice) => (typeof c === 'string' ? Promise.resolve(c) : saveCategory(db, c))

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading', message: 'Signing in…' })
  const [profile, setProfile] = useState<GoogleProfile>()
  const [ctx, setCtx] = useState<RunContext>()
  const [progress, setProgress] = useState<FileProgress[]>([])
  const [running, setRunning] = useState(false)
  const [book, setBook] = useState<Book>()
  const [drill, setDrill] = useState<Filter>()
  const [correcting, setCorrecting] = useState(false)
  const [correctError, setCorrectError] = useState<string>()
  const [reviewStale, setReviewStale] = useState(false)
  const [draft, setDraft] = useState<{ text: string; n: number }>()
  const [saved, setSaved] = useState<Saved>()
  const [analysing, setAnalysing] = useState(false)
  const [reviewProgress, setReviewProgress] = useState<ReviewProgress>()
  const [analysisError, setAnalysisError] = useState<string>()
  /** A finished review the Sheet would not take because sign-in expired while Claude worked. */
  const [unsaved, setUnsaved] = useState<{ context: RunContext; run: AdviceRun }>()
  /** The review in progress: a second request waits for it rather than racing it to the Sheet. */
  const reviewing = useRef<Promise<unknown>>(undefined)
  const started = useRef(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [view, setView] = useState<'dashboard' | 'profile'>('dashboard')
  const [overdue, setOverdue] = useState<Overdue[]>([])
  const [dismissed, setDismissed] = useState<Record<string, string>>(readDismissed)
  /** Bills marked paid on this browser, by bill key; a newer statement is a new key. */
  const [paid, setPaid] = useState<string[]>(() => readList(PAID_KEY))
  /** Statements already re-read for due dates, so one that prints none isn't offered again. */
  const [triedDue, setTriedDue] = useState<string[]>(() => readList(TRIED_DUE_KEY))
  const [readingDue, setReadingDue] = useState<number>()
  const [docUrl, setDocUrl] = useState<string>()
  const [digest, setDigest] = useState<{ ok: boolean; text: string }>()

  const start = useCallback(async () => {
    try {
      setPhase({ kind: 'loading', message: 'Reading settings…' })
      const key = await keyStore.get()
      if (!key) return setPhase({ kind: 'needsKey' })
      setPhase({ kind: 'loading', message: 'Opening your Finance Buddy folder…' })
      const root = await rootFolder()
      const db = await openDatabase(root.id)
      findDoc(root.id, DOC_NAME).then((d) => setDocUrl(d?.webViewLink), () => undefined)
      setCtx({ root, db, anthropic: client(key), meter: sheetMeter(db) })
      setPhase({ kind: 'ready' })
    } catch (error) {
      setPhase({ kind: 'failed', error: (error as Error).message })
    }
  }, [])

  useEffect(() => {
    if (started.current) return
    started.current = true
    auth.restore().then((p) => {
      setProfile(p)
      if (p) start()
      else setPhase({ kind: 'signedOut' })
    })
  }, [start])

  /** Saves a review, then rewrites the Recommendations Doc from it. */
  const publish = useCallback(async (context: RunContext, result: AdviceRun): Promise<{ saved?: Saved; error?: string; docUrl?: string }> => {
    try {
      await saveReview(context.db, result, setReviewProgress)
    } catch (error) {
      // Sign-in can lapse during the minutes Claude takes, and renewing it may
      // need a click. Keep the review so that click can still save it.
      if (error instanceof AuthError) {
        setUnsaved({ context, run: result })
        const message = 'Your Google sign-in expired while Claude was writing the review, so it is not saved yet.'
        setAnalysisError(message)
        return { error: message }
      }
      throw error
    }
    setUnsaved(undefined)
    setBook(await readBook(context.db))
    setReviewStale(false)
    const last = await lastAdvice(context.db)
    setSaved(last)
    if (!last) return {}
    try {
      const [docs, accts] = await Promise.all([context.db.read('Documents'), context.db.read('Accounts')])
      setReviewProgress({ stage: 'saving', note: 'Writing the Recommendations doc' })
      const overdueNow = overdueStatements(docs.rows, accts.rows, formatDate(new Date()))
      const url = await publishDoc(context.root.id, { saved: last, snapshot: result.snapshot, overdue: overdueNow, sheetUrl: context.db.url })
      setDocUrl(url)
      return { saved: last, docUrl: url }
    } catch (error) {
      setAnalysisError(`The review is saved, but the Recommendations doc could not be written: ${(error as Error).message}`)
      return { saved: last }
    }
  }, [])

  /** Runs the review, then publishes it. One at a time: a second request waits for the first. */
  const analyse = useCallback(
    async (context: RunContext): Promise<{ saved?: Saved; error?: string; docUrl?: string }> => {
      while (reviewing.current) await reviewing.current.catch(() => undefined)
      const job = (async () => {
        setAnalysing(true)
        setAnalysisError(undefined)
        try {
          return await publish(context, await review(context.anthropic, context.db, context.meter, setReviewProgress))
        } catch (error) {
          const message = (error as Error).message
          setAnalysisError(message)
          return { error: message }
        } finally {
          setAnalysing(false)
          setReviewProgress(undefined)
        }
      })()
      reviewing.current = job
      try {
        return await job
      } finally {
        if (reviewing.current === job) reviewing.current = undefined
      }
    },
    [publish],
  )

  /** Signs in again (a click, so a popup may open) and saves the kept review. */
  const saveUnsaved = async () => {
    if (!unsaved) return
    setAnalysing(true)
    setAnalysisError(undefined)
    try {
      await auth.signIn()
      await publish(unsaved.context, unsaved.run)
    } catch (error) {
      setAnalysisError(`The review is still not saved: ${(error as Error).message}`)
    } finally {
      setAnalysing(false)
      setReviewProgress(undefined)
    }
  }

  const processAll = useCallback(async (context: RunContext, keep: FileProgress[] = []) => {
    setRunning(true)
    setDigest(undefined)
    try {
      const files: DriveFile[] = await scan(context)
      setProgress([...keep, ...files.map((f): FileProgress => ({ id: f.id, name: f.name, status: 'waiting' }))])
      const results = await run(context, files, (p) => setProgress((list) => list.map((item) => (item.id === p.id ? p : item))))
      const [fresh, last] = await Promise.all([readBook(context.db), lastAdvice(context.db)])
      const snap = fresh.snapshot
      const today = formatDate(new Date())
      const overdueNow = overdueStatements(fresh.tables.documents, fresh.tables.accounts, today)
      setOverdue(overdueNow)
      setBook(fresh)
      setSaved(last)
      // New data, or data that has never been analysed: refresh the advice.
      const changed = results.some((r) => r.status === 'filed' || r.status === 'review')
      const reanalyse = (changed || !last) && snap.accounts.length > 0
      // A run that filed something ends with the digest, sent once the review is in.
      // Not awaited: the inbox is free again while Claude works.
      if (reanalyse || changed) {
        void (async () => {
          const outcome = reanalyse ? await analyse(context) : {}
          const to = auth.profile?.email
          if (!changed || !to) return
          try {
            const doc = outcome.docUrl ?? (await findDoc(context.root.id, DOC_NAME))?.webViewLink
            await sendDigest(to, { today, files: results, overdue: overdueNow, saved: outcome.saved, analysisError: outcome.error, docUrl: doc })
            setDigest({ ok: true, text: `Digest emailed to ${to}` })
          } catch (error) {
            setDigest({ ok: false, text: `The digest email was not sent: ${(error as Error).message}` })
          }
        })()
      }
    } catch (error) {
      setPhase({ kind: 'failed', error: (error as Error).message })
    } finally {
      setRunning(false)
    }
  }, [analyse])

  /** Files dropped on the page go straight into the Drive folder, then a normal run picks them up. */
  const uploadAll = useCallback(
    async (context: RunContext, files: File[]) => {
      if (!files.length) return
      setRunning(true)
      const items: FileProgress[] = files.map((f, i) => ({ id: `upload-${Date.now()}-${i}`, name: f.name, status: 'uploading' }))
      setProgress(items)
      const failed: FileProgress[] = []
      for (const [i, file] of files.entries()) {
        try {
          await uploadFile(context.root.id, file)
          setProgress((list) => list.map((p) => (p.id === items[i].id ? { ...p, status: 'waiting', detail: 'Uploaded to Drive' } : p)))
        } catch (error) {
          const failure: FileProgress = { ...items[i], status: 'error', detail: `Upload failed: ${(error as Error).message}` }
          failed.push(failure)
          setProgress((list) => list.map((p) => (p.id === items[i].id ? failure : p)))
        }
      }
      setRunning(false)
      await processAll(context, failed)
    },
    [processAll],
  )

  // A file dropped anywhere but the drop zone would make the browser open it and leave the app.
  useEffect(() => {
    const stop = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragover', stop)
    window.addEventListener('drop', stop)
    return () => {
      window.removeEventListener('dragover', stop)
      window.removeEventListener('drop', stop)
    }
  }, [])

  const changeStatus = useCallback(
    async (recId: string, status: 'new' | 'done' | 'dismissed') => {
      if (!ctx) return
      setSaved((s) => s && { ...s, recommendations: s.recommendations.map((r) => (r.rec_id === recId ? { ...r, status } : r)) })
      try {
        await setStatus(ctx.db, recId, status)
      } catch (error) {
        setAnalysisError(`Could not save that: ${(error as Error).message}`)
      }
    },
    [ctx],
  )

  // Opening the app is what triggers a run.
  useEffect(() => {
    if (ctx) processAll(ctx)
  }, [ctx, processAll])

  useEffect(() => {
    if (!running && !analysing) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [running, analysing])

  /** Saves a correction or rule, then recounts every figure from the Sheet. */
  const recount = async (save: () => Promise<void>) => {
    if (!ctx) return
    setCorrecting(true)
    setCorrectError(undefined)
    try {
      await save()
      setBook(await readBook(ctx.db))
      setReviewStale(true)
    } catch (error) {
      setCorrectError(`Not saved: ${(error as Error).message}`)
    } finally {
      setCorrecting(false)
    }
  }

  const signIn = async () => {
    try {
      setProfile(await auth.signIn())
      start()
    } catch (error) {
      setPhase({ kind: 'signedOut', error: (error as Error).message })
    }
  }

  const saveKey = async (key: string) => {
    if (!key.startsWith('sk-ant-')) return setPhase({ kind: 'needsKey', error: 'That doesn’t look like an Anthropic API key (sk-ant-…).' })
    await keyStore.set(key)
    start()
  }

  const signOut = async () => {
    await auth.signOut()
    // The next account to sign in on this page must not get this one's Tiller rows.
    tiller.reset()
    setProfile(undefined)
    setCtx(undefined)
    setProgress([])
    setBook(undefined)
    setDrill(undefined)
    setSaved(undefined)
    setDocUrl(undefined)
    setDigest(undefined)
    setView('dashboard')
    setPhase({ kind: 'signedOut' })
  }

  return (
    <>
      <header className="site-header">
        <div className="inner">
          <a href="/" className="wordmark" title={`Finance Buddy ${import.meta.env.VITE_VERSION ?? 'dev'}`}>finance<span>buddy</span></a>
          <nav className="nav" aria-label="Account">
            {phase.kind === 'ready' && ctx && (
              <>
                <button className="navlink" aria-current={view === 'profile' ? 'page' : undefined} onClick={() => setView(view === 'profile' ? 'dashboard' : 'profile')}>
                  Profile
                </button>
                <a href={ctx.root.webViewLink} target="_blank" rel="noreferrer">Folder</a>
                <a href={ctx.db.url} target="_blank" rel="noreferrer">Spreadsheet</a>
                {docUrl && <a href={docUrl} target="_blank" rel="noreferrer">Recommendations</a>}
                <button className="navlink" onClick={() => setPhase({ kind: 'needsKey' })}>Claude key</button>
              </>
            )}
            {profile && (
              <>
                <span className="who">{profile.email}</span>
                <button className="navlink" onClick={signOut}>Sign out</button>
              </>
            )}
            <ThemeToggle />
          </nav>
        </div>
      </header>
      <main>

      {phase.kind === 'loading' && <p className="section muted">{phase.message}</p>}

      {phase.kind === 'signedOut' && (
        <section className="section">
          <p>Finance Buddy reads the statements and receipts you drop into a <b>Finance Buddy</b> folder in your Google Drive, files them, and keeps a spreadsheet of your finances. Everything stays in your Drive.</p>
          <button onClick={signIn}>{auth.hint ? `Continue as ${auth.hint}` : 'Sign in with Google'}</button>
          {phase.error && <p className="error">✕ {phase.error}</p>}
        </section>
      )}

      {phase.kind === 'needsKey' && <KeyForm onSave={saveKey} error={phase.error} />}

      {phase.kind === 'failed' && (
        <section className="section">
          <p className="error">✕ {phase.error}</p>
          <button onClick={start}>Try again</button>
        </section>
      )}

      {phase.kind === 'ready' && ctx && view === 'profile' && (
        <ProfilePage
          db={ctx.db}
          onClose={() => setView('dashboard')}
          onSaved={() => readBook(ctx.db).then(setBook, () => undefined)}
          tillerStatus={book?.tiller}
        />
      )}

      {phase.kind === 'ready' && ctx && (
        // Kept mounted while the profile is open, so a conversation in the question box survives.
        <div hidden={view === 'profile'}>
          {book && (
            <BillsDue
              bills={billsDue(book.tables, formatDate(new Date())).filter((b) => !paid.includes(b.key))}
              missing={missingDueDates(book.tables).filter((id) => !triedDue.includes(id)).length}
              reading={readingDue}
              busy={running || correcting}
              onPaid={(b) => setPaid(writeList(PAID_KEY, [...paid, b.key]))}
              onRead={async () => {
                const ids = missingDueDates(book.tables).filter((id) => !triedDue.includes(id))
                setReadingDue(0)
                try {
                  const failed = await readDueDates(ctx, ids, setReadingDue)
                  setTriedDue(writeList(TRIED_DUE_KEY, [...triedDue, ...ids.filter((id) => !failed.some((f) => f.id === id))]))
                  if (failed.length) setAnalysisError(`Could not read due dates from ${failed.length} statement${failed.length === 1 ? '' : 's'}: ${failed[0].error}`)
                  setBook(await readBook(ctx.db))
                } catch (error) {
                  setAnalysisError(`Could not read due dates: ${(error as Error).message}`)
                } finally {
                  setReadingDue(undefined)
                }
              }}
            />
          )}
          <OverdueBanner
            items={overdue.filter((o) => dismissed[o.key] !== o.last_end)}
            busy={running || correcting}
            onUpload={() => fileInput.current?.click()}
            onClosed={async (o) => {
              setCorrecting(true)
              try {
                const today = formatDate(new Date())
                // The Accounts row for each, however the statement spelled its id.
                const known = (await ctx.db.read('Accounts')).rows.map((r) => String(r.account_id ?? ''))
                const ids = o.account_ids.map((id) => known.find((k) => accountKey(k) === accountKey(id)) ?? id)
                await ctx.db.upsert('Accounts', 'account_id', ids.map((account_id) => ({ account_id, closed: today })))
                const fresh = await readBook(ctx.db)
                setBook(fresh)
                setOverdue(overdueStatements(fresh.tables.documents, fresh.tables.accounts, today))
                setReviewStale(true)
              } catch (error) {
                setAnalysisError(`Could not mark it closed: ${(error as Error).message}`)
              } finally {
                setCorrecting(false)
              }
            }}
            onDismiss={(o) => {
              // Hidden until a newer statement in the series arrives and changes last_end.
              const next = { ...dismissed, [o.key]: o.last_end }
              setDismissed(next)
              try {
                localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
              } catch {
                // Private mode: the dismissal lasts for this page only.
              }
            }}
          />
          <section
            className={`section inbox${dragging ? ' dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              if (!running) setDragging(true)
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              if (!running) uploadAll(ctx, [...e.dataTransfer.files])
            }}
          >
            <div className="row">
              <h2>{running ? 'Processing…' : 'Inbox'}</h2>
              <div className="rec-actions">
                <button disabled={running} onClick={() => fileInput.current?.click()}>↑ Upload files</button>
                <button className="quiet" disabled={running} onClick={() => processAll(ctx)}>Check for new files</button>
              </div>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,text/csv,text/plain,.ofx,.qfx"
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])]
                  e.target.value = ''
                  uploadAll(ctx, files)
                }}
              />
            </div>
            {progress.length === 0 && !running && (
              <p className="dropzone muted">
                {dragging ? 'Drop to upload to your Finance Buddy folder' : 'Drop statements, receipts or tax forms here, or into the Finance Buddy folder in Drive. They upload to Drive and are read straight away.'}
              </p>
            )}
            {dragging && progress.length > 0 && <p className="dropzone muted">Drop to upload to your Finance Buddy folder</p>}
            <ul className="files">
              {progress.map((p) => (
                <li key={p.id} className={`file s-${p.status}`}>
                  <span className="badge"><span aria-hidden>{STATUS[p.status].icon}</span> {STATUS[p.status].label}</span>
                  <span className="name">{p.link ? <a href={p.link} target="_blank" rel="noreferrer">{p.name}</a> : p.name}</span>
                  {p.detail && <span className="detail">{p.detail}</span>}
                </li>
              ))}
            </ul>
            {digest && <p className={digest.ok ? 'saved' : 'error'}>{digest.ok ? '✓' : '✕'} {digest.text}</p>}
          </section>
          {book && (
            <Dashboard
              book={book}
              saved={saved}
              analysing={analysing}
              progress={reviewProgress}
              error={analysisError}
              onRetrySave={unsaved ? saveUnsaved : undefined}
              onRefresh={() => analyse(ctx)}
              onStatus={changeStatus}
              sheetUrl={ctx.db.url}
              onDrill={(f) => {
                setCorrectError(undefined)
                setDrill(f)
              }}
              onAsk={(text) => setDraft((d) => ({ text, n: (d?.n ?? 0) + 1 }))}
              reviewStale={reviewStale}
              askNonce={draft?.n}
            >
              <Ask anthropic={ctx.anthropic} db={ctx.db} draft={draft} meter={ctx.meter} />
            </Dashboard>
          )}
          {book && drill && (
            <TransactionsDrawer
              // A new list remounts, so the rows opened with the old one don't linger.
              key={JSON.stringify(drill)}
              filter={drill}
              ledger={book.ledger}
              rules={book.rules}
              categories={book.categories}
              accountName={(id) => {
                const a = book.snapshot.accounts.find((x) => x.account_id === id)
                return a ? `${a.institution} ${a.name}${a.last4 ? ` ··${a.last4}` : ''}`.trim() : id
              }}
              busy={correcting}
              error={correctError}
              onCorrect={(row: LedgerRow, change: Correction) =>
                recount(async () =>
                  change ? saveCorrection(ctx.db, row, { ...change, category: await categoryId(ctx.db, change.category) }) : clearCorrection(ctx.db, row),
                )
              }
              onRule={(rule: NewRule) => recount(async () => saveRule(ctx.db, { ...rule, category: await categoryId(ctx.db, rule.category) }))}
              onDeleteRule={(id) => recount(() => deleteRule(ctx.db, id))}
              onAsk={(text) => setDraft((d) => ({ text, n: (d?.n ?? 0) + 1 }))}
              onMerchant={(m) => setDrill(merchantFilter(m))}
              onOpen={setDrill}
              onClose={() => setDrill(undefined)}
            />
          )}
        </div>
      )}
      </main>
    </>
  )
}

const DISMISSED_KEY = 'fb.overdueDismissed'
const PAID_KEY = 'fb.billsPaid'
const TRIED_DUE_KEY = 'fb.dueDatesRead'

function readList(key: string): string[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(list) ? list.map(String) : []
  } catch {
    return []
  }
}

/** Saves and returns the list; in a private window it lasts for this page only. */
function writeList(key: string, list: string[]): string[] {
  const kept = [...new Set(list)].slice(-200)
  try {
    localStorage.setItem(key, JSON.stringify(kept))
  } catch {
    // Private mode.
  }
  return kept
}

function readDismissed(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

/** Statements that should have arrived by now; icon, bold and a heavy rule mark them, not hue. */
function OverdueBanner({
  items,
  busy,
  onUpload,
  onDismiss,
  onClosed,
}: {
  items: Overdue[]
  busy: boolean
  onUpload: () => void
  onDismiss: (o: Overdue) => void
  /** Records the accounts as closed in the Accounts tab: no more alerts, and out of today's totals. */
  onClosed: (o: Overdue) => void
}) {
  if (items.length === 0) return null
  return (
    <section className="overdue" aria-label="Statements to upload">
      {items.map((o) => (
        <div key={o.key} className="overdue-item">
          <span className="overdue-icon" aria-hidden>!</span>
          <p>
            <b>Upload a newer {o.name}.</b> The last one on file ended {o.last_end}, {o.days_since} days ago
            {o.missed > 1 ? `; about ${o.missed} statements are missing since then.` : `; the next should have ended around ${o.expected_end}.`}{' '}
            Until {o.missed > 1 ? 'they’re' : 'it’s'} in, figures from {o.key.includes(',') ? 'these accounts' : 'this account'} are out of date.
          </p>
          <div className="rec-actions">
            <button disabled={busy} onClick={onUpload}>↑ Upload</button>
            <button
              className="quiet"
              disabled={busy}
              title="Sets the closed date in the Accounts tab. Clear that cell to undo."
              onClick={() => onClosed(o)}
            >
              {o.key.includes(',') ? 'Accounts closed' : 'Closed or replaced'}
            </button>
            <button className="quiet" onClick={() => onDismiss(o)}>Dismiss</button>
          </div>
        </div>
      ))}
    </section>
  )
}

/** pcarbuddy's toggle: flips the resolved theme and remembers an explicit choice. */
function ThemeToggle() {
  const toggle = () => {
    const root = document.documentElement
    const current = root.dataset.theme ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    const next = current === 'dark' ? 'light' : 'dark'
    root.dataset.theme = next
    try {
      localStorage.setItem('fb-theme', next)
    } catch {
      // Private mode: the choice lasts for this page only.
    }
  }
  return (
    <button className="theme-toggle" type="button" aria-label="Toggle color theme" onClick={toggle}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
      </svg>
    </button>
  )
}

function KeyForm({ onSave, error }: { onSave: (key: string) => void; error?: string }) {
  const [key, setKey] = useState('')
  return (
    <section className="section">
      <h2>Claude API key</h2>
      <p>Finance Buddy uses your own Anthropic key to read documents. It is stored in a hidden app folder in your Google Drive, not on any server.</p>
      <form onSubmit={(e) => { e.preventDefault(); onSave(key) }}>
        <input type="password" autoComplete="off" placeholder="sk-ant-…" value={key} onChange={(e) => setKey(e.target.value)} />
        <button type="submit" disabled={!key}>Save</button>
      </form>
      {error && <p className="error">✕ {error}</p>}
    </section>
  )
}
